import { useEffect, useState } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MyPolicies } from '@grc/types';
import { useAuth, useOrgOptional } from '../lib/auth.js';
import { apiFetch, apiUrl, ApiRequestError } from '../lib/api.js';
import { myPoliciesKey } from '../features/policies/shared.js';
import { Button, Select } from '../components/ui.js';
import { ZestMark } from '../components/ZestMark.js';
import './AppLayout.css';

interface NavItem {
  to: string;
  label: string;
  /** Match this route exactly (NavLink `end`). */
  end?: boolean;
}

interface NavGroup {
  key: string;
  title: string;
  items: NavItem[];
  /** Something in the group needs attention (e.g. an overdue policy): show the dot whether open or not. */
  alert?: string;
}

function isItemActive(item: NavItem, pathname: string): boolean {
  return item.end ? pathname === item.to : pathname.startsWith(item.to);
}

/**
 * A collapsible sidebar section. Groups start collapsed (decluttered) unless
 * they hold the current page or the user last left them open; navigating into
 * a group always reveals it, and a collapsed group holding the current page
 * shows a dot so it is still clear where you are.
 */
function NavGroupSection({ group }: { group: NavGroup }) {
  const location = useLocation();
  const containsActive = group.items.some((item) => isItemActive(item, location.pathname));
  const [open, setOpen] = useState<boolean>(() => {
    const stored = localStorage.getItem(`za.nav.${group.key}`);
    return stored !== null ? stored === '1' : containsActive;
  });
  useEffect(() => {
    if (containsActive) setOpen(true);
  }, [containsActive]);

  const toggle = () =>
    setOpen((current) => {
      localStorage.setItem(`za.nav.${group.key}`, current ? '0' : '1');
      return !current;
    });

  return (
    <div className="nav-group">
      <button
        type="button"
        className="nav-group-toggle"
        aria-expanded={open}
        onClick={toggle}
      >
        <span className={`nav-chevron ${open ? 'open' : ''}`} aria-hidden="true" />
        {group.title}
        {group.alert ? (
          <span className="nav-group-here" role="img" aria-label={group.alert} title={group.alert} />
        ) : (
          !open && containsActive && <span className="nav-group-here" aria-hidden="true" />
        )}
      </button>
      {open && (
        <div className="nav-group-items">
          {group.items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className="nav-link">
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * An organisation that restricts sign-in providers answers every org-scoped
 * request with a 403 carrying `reason: idp_not_allowed` when the member signed
 * in another way. Watch the query cache for it so the shell can explain once,
 * instead of every page failing silently.
 */
function useProviderNotice(orgId: string | undefined): string | null {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => setNotice(null), [orgId]);
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.type !== 'updated' || event.action.type !== 'error') return;
        const err = event.action.error;
        if (err instanceof ApiRequestError && err.details?.reason === 'idp_not_allowed') {
          setNotice(err.message);
        }
      }),
    [queryClient],
  );
  return notice;
}

export function AppLayout() {
  const { me, logout, stopImpersonation } = useAuth();
  const { org, orgs, setOrgId } = useOrgOptional();

  const role = org?.role;
  const isAdmin = role === 'admin';
  const isAssessor = role === 'assessor';
  const isReviewer = role === 'reviewer';
  const isSystemAdmin = me?.user.isSystemAdmin ?? false;
  const impersonating = me?.impersonating ?? null;
  const providerNotice = useProviderNotice(org?.orgId);
  // Overdue policies light the Policies group (same cache as the My policies page).
  const myPolicies = useQuery({
    queryKey: myPoliciesKey(org?.orgId ?? ''),
    queryFn: () => apiFetch<MyPolicies>(`/orgs/${org!.orgId}/my-policies`),
    enabled: !!org && !isAssessor,
    staleTime: 60_000,
  });
  const overduePolicies = myPolicies.data?.counts.overdue ?? 0;

  const assessorLinks: NavItem[] = isAssessor
    ? [
        { to: '/assessor', label: 'Assessor overview', end: true },
        { to: '/assessor/reviews', label: 'Items to review' },
      ]
    : [];

  const topLinks: NavItem[] = [
    { to: '/', label: 'Dashboard', end: true },
    { to: '/assessments', label: 'Assessments' },
    ...(!isAssessor
      ? [
          { to: '/corpus', label: 'Shared corpus' },
          { to: '/evidence', label: 'Evidence' },
        ]
      : []),
    { to: '/standards', label: 'Standards' },
    ...(!isAssessor ? [{ to: '/internal-audits', label: 'Internal Audits' }] : []),
  ];

  const groups: NavGroup[] = [];
  if (isAssessor) {
    groups.push({ key: 'assessor', title: 'Assessor', items: assessorLinks });
  }
  if (!isAssessor) {
    groups.push(
      {
        key: 'inventory',
        title: 'Inventory',
        items: [
          { to: '/assets', label: 'All Assets', end: true },
          { to: '/assets/products', label: 'Company Products' },
          { to: '/assets/information-assets', label: 'Information Assets' },
          { to: '/assets/software', label: 'Software', end: true },
          { to: '/assets/software/access', label: 'My Software Access' },
          // Review items carry colleagues' names, emails and access levels.
          ...(isAdmin || isReviewer || isSystemAdmin
            ? [{ to: '/assets/software/access-reviews', label: 'Access Reviews' }]
            : []),
          { to: '/assets/hardware', label: 'Hardware', end: true },
          { to: '/assets/hardware/manage', label: 'Manage Hardware' },
          { to: '/assets/suppliers', label: 'Suppliers' },
          { to: '/assets/websites', label: 'Websites' },
        ],
      },
      {
        key: 'registries',
        title: 'Registries',
        items: [
          { to: '/registers', label: 'All Registers', end: true },
          { to: '/registers/risks', label: 'Risk Registry' },
          { to: '/registers/vulnerabilities', label: 'Vulnerabilities' },
          { to: '/registers/incidents', label: 'Incidents' },
          { to: '/registers/gdpr-requests', label: 'GDPR Requests' },
          { to: '/registers/dpias', label: 'DPIAs', end: true },
          { to: '/registers/dpias/coverage', label: 'DPIA coverage' },
          { to: '/registers/corrective-actions', label: 'Corrective Actions' },
          { to: '/registers/processing', label: 'Processing Registry' },
          { to: '/registers/change-requests', label: 'Change Requests' },
        ],
      },
      {
        key: 'training',
        title: 'Training',
        items: [
          { to: '/training/my-courses', label: 'My courses' },
          ...(isAdmin || isReviewer || isSystemAdmin
            ? [{ to: '/training/track', label: 'Track training' }]
            : []),
        ],
      },
      {
        key: 'policies',
        title: 'Policies',
        alert:
          overduePolicies > 0
            ? `${overduePolicies} overdue ${overduePolicies === 1 ? 'policy' : 'policies'} to accept`
            : undefined,
        items: [
          { to: '/policies', label: 'My policies', end: true },
          ...(isAdmin || isReviewer || isSystemAdmin
            ? [
                { to: '/policies/register', label: 'Document register' },
                { to: '/policies/tracking', label: 'Policy acceptance' },
              ]
            : []),
        ],
      },
      {
        key: 'organisation',
        title: 'Organisation',
        items: [
          { to: '/exports', label: 'Exports' },
          { to: '/import', label: 'Import & migrate' },
          ...(isAdmin
            ? [
                { to: '/settings', label: 'Settings', end: true },
                { to: '/settings/members', label: 'Members' },
                { to: '/settings/billing', label: 'Billing' },
                { to: '/settings/branding', label: 'Branding' },
                { to: '/settings/usage', label: 'Usage' },
                { to: '/settings/api', label: 'API & webhooks' },
                { to: '/audit', label: 'Audit log' },
              ]
            : []),
        ],
      },
    );
  }
  if (isSystemAdmin) {
    groups.push({
      key: 'platform',
      title: 'Platform',
      items: [
        { to: '/admin', label: 'Overview', end: true },
        { to: '/admin/orgs', label: 'Manage orgs' },
        { to: '/admin/standards', label: 'Manage standards' },
        { to: '/admin/audit', label: 'Audit log' },
      ],
    });
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Link to="/" className="sidebar-brand">
          <ZestMark className="logo" />
          Zest Assure
        </Link>

        {orgs.length > 1 && (
          <div>
            <label className="sr-only" htmlFor="org-switcher">
              Organisation
            </label>
            <Select
              id="org-switcher"
              value={org?.orgId ?? ''}
              onChange={(e) => setOrgId(e.target.value)}
            >
              {orgs.map((o) => (
                <option key={o.orgId} value={o.orgId}>
                  {o.orgName}
                  {o.role === 'assessor' ? ' (assessor)' : ''}
                </option>
              ))}
            </Select>
          </div>
        )}

        <nav aria-label="Main navigation">
          {impersonating && (
            <button
              type="button"
              className="nav-link nav-exit-impersonation"
              onClick={() => void stopImpersonation()}
            >
              ← Exit impersonation
            </button>
          )}
          {topLinks.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className="nav-link">
              {item.label}
            </NavLink>
          ))}
          {groups.map((group) => (
            <NavGroupSection key={group.key} group={group} />
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-row">
            <Link to="/profile" className="user-link" title="My profile">
              <span className="user-avatar" aria-hidden="true">
                {me?.user.avatarUrl ? (
                  <img src={apiUrl(`/users/${me.user.id}/avatar`)} alt="" />
                ) : (
                  initials(me?.user.name ?? '')
                )}
              </span>
              <span className="user-meta">
                <span className="user-name">{me?.user.name}</span>
                <span className="user-email">{me?.user.email}</span>
              </span>
            </Link>
            <Button variant="secondary" size="sm" onClick={() => void logout()}>
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        {impersonating && (
          <div className="impersonation-banner" role="status">
            <span>
              Viewing as <strong>{me?.user.name}</strong> ({me?.user.email}) — you are{' '}
              {impersonating.by.name}
            </span>
            <button type="button" onClick={() => void stopImpersonation()}>
              Exit impersonation
            </button>
          </div>
        )}
        {providerNotice && (
          <div className="provider-banner" role="alert">
            <span>
              {providerNotice}. Sign out and sign in again with that provider to use{' '}
              <strong>{org?.orgName}</strong>.
            </span>
            <button type="button" onClick={() => void logout()}>
              Sign out
            </button>
          </div>
        )}
        <div className="main-inner">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
